import {
	type BalanceSheetId,
	type FinancialPeriodId,
	type IncomeStatementId,
	type ISODate,
	PeriodType,
	StatementType,
} from "@inrev/common";
import {
	ActiveStatementTypeEnum,
	type AnalysisPeriodSelectorItem,
	type BalanceSheetPeriodSelectorItem,
	type IncomeStatementPeriodSelectorItem,
	type PeriodSelectorItem,
} from "../types";
import { computeHighestQualityFyeDefaultPeriods } from "./computeHighestQualityFyeDefaultPeriods";

/**
 * The default-selection logic discriminates FYE from interim via `periodType`. Income statements
 * carry it directly; balance sheets express the same distinction through `statementType`.
 */
const defaultPeriodType = (item: PeriodSelectorItem): PeriodType =>
	item.type === ActiveStatementTypeEnum.BalanceSheet
		? item.statementType === StatementType.Fye
			? PeriodType.Fye
			: PeriodType.Other
		: item.periodType;

const asSelectionCandidates = <TItem extends PeriodSelectorItem>(
	items: TItem[],
): Array<TItem & { periodEndDate: ISODate; periodType: PeriodType }> =>
	items
		.filter((item) => item.isActive)
		.map((item) => ({
			...item,
			periodEndDate: item.date,
			periodType: defaultPeriodType(item),
		}));

/** Default period rows for the statement matrix period selector (matches legacy DEFAULT preset). */
export const getDefaultPeriodSelectorItems = <TItem extends PeriodSelectorItem>(
	items: TItem[],
): TItem[] => computeHighestQualityFyeDefaultPeriods(asSelectionCandidates(items));

const defaultIds = <TItem extends PeriodSelectorItem>(items: TItem[]): Set<TItem["id"]> =>
	new Set(getDefaultPeriodSelectorItems(items).map((item) => item.id));

export const computeDefaultBalanceSheetPeriodSelectorIds = (
	items: BalanceSheetPeriodSelectorItem[],
): Set<BalanceSheetId> => defaultIds(items);

export const computeDefaultIncomeStatementPeriodSelectorIds = (
	items: IncomeStatementPeriodSelectorItem[],
): Set<IncomeStatementId> => defaultIds(items);

export const computeDefaultAnalysisPeriodSelectorIds = (
	items: AnalysisPeriodSelectorItem[],
): Set<FinancialPeriodId> => defaultIds(items);
